
import {of as observableOf, throwError as observableThrowError, Subject, Observable, pipe} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import { Router } from '@angular/router';
import { Token } from './model/token.model';
import { User } from './model/user.model';
import { Authority } from './model/authority.model';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {ApiMessageService, MessageType} from '../core/api-message.service';
import {Injectable} from '@angular/core';
import {ConfigService} from '../core/config.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';


@Injectable()
export class AuthService {

  static tokenHeaders = new HttpHeaders()
    .set('Content-Type', 'application/x-www-form-urlencoded')
    .set('Authorization', 'Basic YWNlOnRoaXNpc3NlY3JldA==');

  // Emesso quando l'utente effettua login
  public userActivated = new Subject<User>();
  private oidcEnable = false;

  // Stored Token
  private token: Token;

  public redirectUrl: string;

  constructor(private router: Router,
              private httpClient: HttpClient,
              private apiMessageService: ApiMessageService,
              private configService: ConfigService,
              private oidcSecurityService: OidcSecurityService) {
    this.oidcEnable = (environment.oidc.enable === 'true') ? true : false;
  }

  private setToken(token: Token) {
    this.token = token;
    if (this.token == null) {
      localStorage.removeItem('ace_token');
    } else {
      localStorage.setItem('ace_token', JSON.stringify(this.token));
    }
  }

  /**
   * Il token.
   * @returns {Token}
   */
  public getToken(): Token {
    if (this.token) {
      return this.token;
    }
    this.token = JSON.parse(localStorage.getItem('ace_token'));
    if (this.token) {
      return this.token;
    }
    return null;
  }

  /**
   * Se il token è scaduto.
   * @returns {Boolean}
   */
  public isTokenExpired(): Boolean {
    return this.getToken().valid_until <= new Date().getTime();
  }

  /**
   * Login.
   * In caso positivo ritorna il token, altrimenti l'errore.
   * @param username
   * @param password
   */
  public signinUser(username: string, password: string): Observable<any> {
    const post = 'grant_type=password&scope=webclient&username=' + username + '&password=' + encodeURIComponent(password);

    return this.configService.getApiBase().pipe(switchMap((apiBase) => {

      return this.httpClient.post<Token>(apiBase + ConfigService.URL_OAUTH, post,
        {headers: AuthService.tokenHeaders}).pipe(
        map(
          (token) => {
            token.valid_until = new Date().getTime() + (token.expires_in * 1000);
            this.setToken(token);
            this.userActivated.next(this.token.user); // ex. per notifica all'headerComponent.
            return this.getToken();
          }),
        catchError(
          (error: HttpErrorResponse) => {
            this.apiMessageService.sendMessage(MessageType.ERROR, error.error.error_description);
            return observableThrowError(error);
          }));
    }));

  }

  private refreshToken(): Observable<any> {

    console.log('preparo il nuovo token');

    const post = 'grant_type=refresh_token&scope=webclient&refresh_token=' + this.getToken().refresh_token;

    return this.configService.getApiBase().pipe(switchMap((apiBase) => {

      return this.httpClient.post<Token>(apiBase + ConfigService.URL_OAUTH, post,
        {headers: AuthService.tokenHeaders}).pipe(
        map(
          (token) => {
            // this.apiMessageService.sendMessage(MessageType.WARNING, 'Token ricaricato');
            token.valid_until = new Date().getTime() + (token.expires_in * 1000);
            this.setToken(token);
            return this.getToken();
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status == 401) {
              this.logout();
            } else {
              return observableThrowError(error);              
            }
          })
          );
    }));
    pipe();

  }

  /**
   * Logout.
   */
  public logout() {
    if (this.oidcEnable) {
      this.oidcSecurityService.logoffAndRevokeTokens().subscribe(() => {
        this.userActivated.next(null);
        this.router.navigate(['']);  
      });
    } else {
      this.setToken(null);
      this.userActivated.next(null);
      this.router.navigate(['']);  
    } 
  }

  /**
   * Recupera il token (refreshato in caso di bisogno).
   * @returns {Observable<Token>}
   */
  public getRefreshedToken(): Observable<Token> {
    if (this.oidcEnable) {
      return this.oidcSecurityService.getAccessToken().pipe(
        map((access_token) => {
          return new Token(
            access_token,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
          );
        })
      );
    } else {
      if (this.getToken() == null) {
        return observableOf(null);
      }
      if (!this.isTokenExpired()) {
        return observableOf(this.getToken());
      }
      return this.refreshToken();  
    }
  }

  /**
   * If User is autenticated.
   * @returns {boolean}
   */
  public isAuthenticated(): Observable<boolean>{
    if (this.oidcEnable) {
      return this.oidcSecurityService.isAuthenticated();
    } else {
      return observableOf(this.getToken() != null);
    }
  }

  public checkAuth() {
    if (this.oidcEnable) {
      return this.oidcSecurityService.checkAuth().pipe(map(({isAuthenticated}) => {
        return isAuthenticated;
      }));
    } else {
      return observableOf(this.getToken() != null);
    }
  }

  /**
   * Authenticated User
   * @returns {any}
   */
  public getUser(): Observable<User> {
    if (this.oidcEnable) {
      return this.oidcSecurityService.getUserData().pipe(map((userData) => {
        console.log(userData);
        var user = new User(
          userData?.auth_time,
          userData?.username_cnr || userData?.preferred_username||' ',
          userData?.given_name||' ',
          userData?.family_name||' ',
          userData?.email||' ',
          true,
          true,
          true,
          true
        );
        return user;
      }));
    } else {
      return observableOf(this.getToken() ? this.getToken().user : null);
    }
  }

  /**
   * Authenticated User's authorities.
   * @returns {any}
   */
  public getAuthorities(): Authority[] {
    return this.getToken() ? this.getToken().auth : null;
  }

  /**
   * Authenticate username.
   * @returns {string}
   */
  public getUsername(): Observable<string> {
      return this.getUser().pipe(map((user: User) => {
        return user.username;
      }));
  }
}
